import React from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, IconButton, Typography, Chip,
  Button, Checkbox, Fade, MenuItem, TableSortLabel, Collapse,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ModuleColumn } from '../../types';
import { RADIUS, brandColors, TYPE, brandCss, APP_FONT_FAMILY } from '../../theme/appTheme';
import { tableHeadRowSx, tableHeadCellSx, tableSortLabelSx, tableHeadTextSx, tableBodyCellSx, tableContainerSx } from '../../theme/tableStyles';
import { toolbarTextFieldSx, tableActionButtonSx } from '../../theme/formStyles';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../constants/tableDefaults';
import { TableSkeleton } from '../ui/TableSkeleton';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { buildExportColumns, exportRowsToExcel } from '../../utils/exportToExcel';
import { TableColumnPicker } from './TableColumnPicker';
import { TableImportExportMenus } from './TableImportExportMenus';

export interface DataTableFilter {
  key: string;
  label: string;
  options: { label: string; value: string }[];
  getValue: (row: Record<string, unknown>) => string;
}

/** Only these categorical fields get toolbar dropdown filters (not every column). */
const IMPORTANT_FILTER_FIELDS = new Set([
  'vehicleType', 'typeName', 'fuelType', 'size', 'ownerType', 'brand', 'makeName',
  'tripType', 'branch', 'branchName', 'paymentMode', 'category', 'role',
  'department', 'ownership', 'documentType', 'vendorName',
]);

/** Build up to 3 important categorical filters from row data. */
export function buildAutoToolbarFilters(
  columns: ModuleColumn[],
  rows: Record<string, unknown>[],
  max = 3,
): DataTableFilter[] {
  const filters: DataTableFilter[] = [];
  for (const col of columns) {
    if (!IMPORTANT_FILTER_FIELDS.has(col.field)) continue;
    if (col.format === 'date' || col.format === 'datetime' || col.format === 'currency' || col.format === 'number') continue;
    const values = Array.from(new Set(
      rows.map((row) => {
        const raw = col.searchValue ? col.searchValue(row) : String(row[col.field] ?? '');
        return String(raw || '').trim();
      }).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b));
    if (values.length < 2 || values.length > 24) continue;
    filters.push({
      key: col.field,
      label: col.header,
      options: values.map((v) => ({ label: v, value: v })),
      getValue: (row) => {
        const raw = col.searchValue ? col.searchValue(row) : String(row[col.field] ?? '');
        return String(raw || '').trim();
      },
    });
    if (filters.length >= max) break;
  }
  return filters;
}

interface ColumnPickerConfig {
  allColumns: ModuleColumn[];
  visibleFields: string[];
  defaultFields: string[];
  onSave: (fields: string[]) => void | Promise<void>;
  onReset?: () => void | Promise<void>;
  isCustomized?: boolean;
}

interface DataTableProps {
  columns: ModuleColumn[];
  rows: Record<string, unknown>[];
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onEdit?: (row: Record<string, unknown>) => void;
  onDelete?: (row: Record<string, unknown>) => void;
  onView?: (row: Record<string, unknown>) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
  onBulkDelete?: (ids: string[]) => void | Promise<void>;
  onBulkStatusChange?: (ids: string[], status: string) => void | Promise<void>;
  onExport?: () => void;
  onImport?: (records: Record<string, unknown>[]) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canView?: boolean;
  canImport?: boolean;
  canExport?: boolean;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  recordLabel?: string;
  columnPicker?: ColumnPickerConfig;
  selectable?: boolean;
  error?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  getRowId?: (row: Record<string, unknown>) => string;
  getStatus?: (row: Record<string, unknown>) => string;
  statusOptions?: { label: string; value: string }[];
  forceStatusFilter?: boolean;
  renderActions?: (row: Record<string, unknown>) => React.ReactNode;
  /** @deprecated Field filters UI removed — kept for call-site compatibility */
  enableColumnFilters?: boolean;
  enableExcelExport?: boolean;
  exportFileName?: string;
  exportSheetName?: string;
  onSelectionChange?: (ids: string[]) => void;
  /** Espo-style inline detail panel toggled by row click / chevron */
  renderExpandedRow?: (row: Record<string, unknown>) => React.ReactNode;
  exportExtraColumns?: { id: string; header: string; exportValue?: (row: Record<string, unknown>) => unknown; searchValue?: (row: Record<string, unknown>) => string }[];
  toolbarFilters?: DataTableFilter[];
  showRowNumber?: boolean;
  title?: string;
}

const StatusChip: React.FC<{ value: unknown }> = ({ value }) => {
  const v = String(value || 'active').toLowerCase();
  const colors: Record<string, { bg: string; color: string; label?: string; dot: string }> = {
    active: { bg: '#dcfce7', color: '#166534', label: 'Active', dot: '#16a34a' },
    completed: { bg: '#dcfce7', color: '#166534', label: 'Completed', dot: '#16a34a' },
    paid: { bg: '#dcfce7', color: '#166534', label: 'Paid', dot: '#16a34a' },
    pending: { bg: '#fef3c7', color: '#d97706', label: 'New', dot: '#f59e0b' },
    created: { bg: '#f1f5f9', color: '#475569', label: 'New', dot: '#94a3b8' },
    assigned: { bg: '#e0e7ff', color: '#4f46e5', label: 'Assigned', dot: '#6366f1' },
    inactive: { bg: '#fee2e2', color: '#dc2626', label: 'Inactive', dot: '#dc2626' },
    cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled', dot: '#dc2626' },
    in_progress: { bg: '#dbeafe', color: brandColors.info, label: 'Ongoing', dot: brandColors.info },
    maintenance: { bg: '#ffedd5', color: '#c2410c', label: 'Maintenance', dot: '#ea580c' },
    approved: { bg: '#dcfce7', color: '#166534', label: 'Approved', dot: '#16a34a' },
  };
  const c = colors[v] || colors.active;
  return (
    <Chip
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.dot }} />
          {c.label || v.replace('_', ' ')}
        </Box>
      }
      size="small"
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: 11, height: 22, textTransform: 'none', '& .MuiChip-label': { px: 0.75 } }}
    />
  );
};

const getDisplayName = (row: Record<string, unknown>, col: ModuleColumn) => {
  const val = row[col.field];
  return String(val ?? '—');
};

export const DataTable: React.FC<DataTableProps> = ({
  columns, rows, loading, search, onSearchChange, page, rowsPerPage,
  onPageChange, onRowsPerPageChange, onEdit, onDelete, onView, onRowClick, onBulkDelete, onBulkStatusChange, onExport, onImport,
  canEdit, canDelete, canView, canImport, canExport,
  defaultRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  recordLabel = 'records',
  columnPicker,
  selectable = true,
  error,
  emptyMessage = 'No records found',
  searchPlaceholder = 'Search...',
  getRowId,
  getStatus,
  statusOptions,
  forceStatusFilter,
  renderActions,
  enableColumnFilters: _enableColumnFilters,
  enableExcelExport,
  exportFileName = 'export',
  exportSheetName = 'Data',
  onSelectionChange,
  renderExpandedRow,
  exportExtraColumns = [],
  toolbarFilters = [],
  showRowNumber = true,
  title,
}) => {
  const { brand } = useAppTheme();
  const { formatCurrency, formatDate } = useLocale();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [extraFilterValues, setExtraFilterValues] = React.useState<Record<string, string>>({});
  const [sortBy, setSortBy] = React.useState<string>(columns[0]?.field || '');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [actionMenu, setActionMenu] = React.useState<{ anchor: HTMLElement; row: Record<string, unknown> } | null>(null);

  const rowId = (row: Record<string, unknown>) => getRowId?.(row) ?? String(row._id ?? row.id ?? row.key ?? '');
  const rowStatus = (row: Record<string, unknown>) => (
    getStatus?.(row) ?? String(row.status || row.paymentStatus || 'active')
  ).toLowerCase();

  const hasStatusCol = columns.some((c) => c.field === 'status' || c.field === 'paymentStatus');
  const showStatusFilter = forceStatusFilter || hasStatusCol || Boolean(statusOptions && statusOptions.length > 1);

  const filtered = rows.filter((row) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || Object.values(row).some((v) =>
      String(v ?? '').toLowerCase().includes(q)
    ) || columns.some((col) => {
      const val = col.searchValue ? col.searchValue(row) : '';
      return val.toLowerCase().includes(q);
    }) || exportExtraColumns.some((col) => {
      const val = col.searchValue ? col.searchValue(row) : '';
      return val.toLowerCase().includes(q);
    });
    const matchesStatus = statusFilter === 'all' || rowStatus(row) === statusFilter;
    const matchesExtra = toolbarFilters.every((filter) => {
      const selectedValue = extraFilterValues[filter.key] || 'all';
      if (selectedValue === 'all') return true;
      return filter.getValue(row).toLowerCase() === selectedValue.toLowerCase();
    });
    return matchesSearch && matchesStatus && matchesExtra;
  });

  const sorted = React.useMemo(() => {
    if (!sortBy) return filtered;
    const col = columns.find((c) => c.field === sortBy);
    const normalizeValue = (v: unknown) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'number') return v;
      if (v instanceof Date) return v.getTime();
      const text = String(v).trim();
      if (!text) return '';
      const numeric = Number(text.replace(/,/g, ''));
      if (!Number.isNaN(numeric) && /^-?\d+(\.\d+)?$/.test(text.replace(/,/g, ''))) return numeric;
      const dateMs = Date.parse(text);
      if (!Number.isNaN(dateMs) && /[-/:]/.test(text)) return dateMs;
      return text.toLowerCase();
    };
    return [...filtered].sort((a, b) => {
      const av = col?.sortValue ? col.sortValue(a) : normalizeValue(a[sortBy]);
      const bv = col?.sortValue ? col.sortValue(b) : normalizeValue(b[sortBy]);
      const result = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filtered, sortBy, sortDirection, columns]);

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const pageIds = paginated.map((r) => rowId(r));
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const somePageSelected = pageIds.some((id) => selected.includes(id));

  const selectedRows = React.useMemo(
    () => sorted.filter((row) => selected.includes(rowId(row))),
    [sorted, selected, getRowId],
  );

  const exportCols = React.useMemo(() => {
    const cols = columns
      .filter((c) => c.field !== '_actions')
      .map((c) => ({
        id: c.field,
        header: c.header,
        exportValue: c.exportValue,
        searchValue: c.searchValue,
      }));
    exportExtraColumns.forEach((c) => {
      if (!cols.some((x) => x.id === c.id)) {
        cols.push({
          id: c.id,
          header: c.header,
          exportValue: c.exportValue,
          searchValue: c.searchValue,
        });
      }
    });
    return cols;
  }, [columns, exportExtraColumns]);

  const runExcelExport = (rowsToExport: Record<string, unknown>[], suffix: string) => {
    if (!rowsToExport.length) return;
    exportRowsToExcel({
      columns: buildExportColumns(exportCols),
      rows: rowsToExport,
      fileName: `${exportFileName}-${suffix}`,
      sheetName: exportSheetName,
    });
  };

  const showImportExport = Boolean(canImport || canExport || enableExcelExport || onExport);

  const toggleAll = () => {
    const next = allPageSelected
      ? selected.filter((id) => !pageIds.includes(id))
      : Array.from(new Set([...selected, ...pageIds]));
    setSelected(next);
    onSelectionChange?.(next);
  };

  const toggleOne = (id: string) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    setSelected(next);
    onSelectionChange?.(next);
  };

  const handleSort = (field: string) => {
    if (!field) return;
    if (sortBy === field) setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(field);
      setSortDirection('asc');
    }
    onPageChange(0);
  };

  const handleBulkDelete = async () => {
    if (!selected.length || !onBulkDelete) return;
    if (!window.confirm(`Delete ${selected.length} selected record(s)?`)) return;
    await onBulkDelete(selected);
    setSelected([]);
  };

  const handleBulkStatus = async (status: string) => {
    if (!selected.length || !onBulkStatusChange) return;
    await onBulkStatusChange(selected, status);
    setSelected([]);
  };

  const renderCell = (col: ModuleColumn, row: Record<string, unknown>, colIndex: number) => {
    if (col.render) return col.render(row);
    if (col.field === 'status' || col.field === 'paymentStatus') return <StatusChip value={row[col.field]} />;
    const raw = row[col.field];
    const cellSx = {
      fontSize: TYPE.tableContent.fontSize,
      fontWeight: 500,
      color: brandColors.textDark,
      lineHeight: TYPE.tableContent.lineHeight,
    };
    if (col.format === 'currency' && raw !== null && raw !== undefined && raw !== '') {
      return (
        <Typography variant="body2" sx={cellSx}>
          {formatCurrency(Number(raw))}
        </Typography>
      );
    }
    if ((col.format === 'date' || col.format === 'datetime') && raw) {
      const formatted = formatDate(String(raw), col.format === 'datetime'
        ? { dateStyle: 'medium', timeStyle: 'short' }
        : { dateStyle: 'medium' });
      return (
        <Typography variant="body2" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
          {formatted}
        </Typography>
      );
    }
    if (col.format === 'number' && raw !== null && raw !== undefined && raw !== '') {
      return (
        <Typography variant="body2" sx={cellSx}>
          {Number(raw).toLocaleString()}
        </Typography>
      );
    }
    if (colIndex === 0) {
      const name = getDisplayName(row, col);
      return (
        <Typography variant="body2" sx={{ ...cellSx, fontWeight: 600 }}>
          {name}
        </Typography>
      );
    }
    return <Typography variant="body2" sx={cellSx}>{String(row[col.field] ?? '—')}</Typography>;
  };

  const hasActions = Boolean(renderActions || canView || canEdit || canDelete);
  const expandable = Boolean(renderExpandedRow);
  const showCheckboxes = selectable;
  const colSpan = columns.length
    + (expandable ? 1 : 0)
    + (hasActions ? 1 : 0)
    + (showCheckboxes ? 1 : 0)
    + (showRowNumber ? 1 : 0);

  const hasActiveFilters = Boolean(
    search
    || statusFilter !== 'all'
    || toolbarFilters.some((f) => (extraFilterValues[f.key] || 'all') !== 'all'),
  );

  const resetFilters = () => {
    onSearchChange('');
    setStatusFilter('all');
    setExtraFilterValues({});
    onPageChange(0);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (error) {
    return (
      <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.danger}`, bgcolor: '#fef2f2' }}>
        <Typography color="error" fontSize={13} fontWeight={600}>{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{
      borderRadius: 2.5,
      overflow: 'hidden',
      border: `1px solid ${brandColors.border}`,
      bgcolor: '#fff',
      boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)',
    }}>
      {/* Bulk action bar */}
      <Fade in={selectable && selected.length > 0}>
        <Box sx={{
          display: selected.length ? 'flex' : 'none', alignItems: 'center', flexWrap: 'wrap',
          gap: { xs: 0.75, sm: 1.5 },
          px: { xs: 1.5, sm: 2.5 }, py: 1.2, bgcolor: brand.green50, borderBottom: `2px solid ${brand.primary}`,
        }}>
          <CheckCircleOutlineIcon sx={{ color: brand.primary, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: brand.primaryDark }}>
            {selected.length} selected
          </Typography>
          {canEdit && onBulkStatusChange && (
            <>
              <Button size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleBulkStatus('active')}
                sx={{ borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600, bgcolor: brand.primary }}>
                Activate
              </Button>
              <Button size="small" variant="outlined" startIcon={<BlockIcon />}
                onClick={() => handleBulkStatus('inactive')}
                sx={{ borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600, borderColor: brandColors.danger, color: brandColors.danger }}>
                Deactivate
              </Button>
            </>
          )}
          {canDelete && onBulkDelete && (
            <Button size="small" variant="contained" color="error" startIcon={<DeleteSweepIcon />}
              onClick={handleBulkDelete} sx={{ ml: 1, borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600 }}>
              Delete Selected
            </Button>
          )}
          {canEdit && selected.length === 1 && onEdit && (
            <Button size="small" variant="contained" startIcon={<EditOutlinedIcon />}
              onClick={() => {
                const row = rows.find((r) => String(r._id) === selected[0]);
                if (row) onEdit(row);
              }}
              sx={{ borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600, bgcolor: brandCss.primary }}>
              Edit Selected
            </Button>
          )}
          {(enableExcelExport || canExport) && selectedRows.length > 0 && (
            <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
              onClick={() => runExcelExport(selectedRows, 'selected')}
              sx={{ borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600, borderColor: brand.primary, color: brand.primary }}>
              Export Selected
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => { setSelected([]); onSelectionChange?.([]); }} sx={{ color: brandColors.textMuted }}>Clear</Button>
        </Box>
      </Fade>

      {/* Left: search + filters · Right: Columns / Import / Export */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          width: '100%',
          columnGap: 1.5,
          rowGap: 0.75,
          px: { xs: 1.25, sm: 1.5 },
          pt: 1.5,
          pb: 1,
          overflow: 'visible',
          borderBottom: `1px solid ${brandColors.border}`,
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 420 },
              minWidth: { xs: 0, sm: 360 },
              maxWidth: { xs: '100%', sm: 480 },
              flex: '0 0 auto',
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              InputLabelProps={{ shrink: false }}
              sx={{
                ...toolbarTextFieldSx,
                width: '100% !important',
                maxWidth: '100%',
              }}
            />
          </Box>
          {toolbarFilters.map((filter) => (
            <TextField
              key={filter.key}
              select
              size="small"
              value={extraFilterValues[filter.key] || 'all'}
              onChange={(e) => {
                setExtraFilterValues((prev) => ({ ...prev, [filter.key]: e.target.value }));
                onPageChange(0);
              }}
              InputLabelProps={{ shrink: false }}
              SelectProps={{ displayEmpty: true }}
              sx={{
                ...toolbarTextFieldSx,
                width: '120px !important',
                minWidth: 120,
                maxWidth: 120,
                flex: '0 0 120px',
              }}
            >
              <MenuItem value="all" sx={{ fontSize: 12 }}>{filter.label}</MenuItem>
              {filter.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>{opt.label}</MenuItem>
              ))}
            </TextField>
          ))}
          {showStatusFilter && (
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); onPageChange(0); }}
              InputLabelProps={{ shrink: false }}
              SelectProps={{ displayEmpty: true }}
              sx={{
                ...toolbarTextFieldSx,
                width: '120px !important',
                minWidth: 120,
                maxWidth: 120,
                flex: '0 0 120px',
              }}
            >
              {(statusOptions || [
                { label: 'Status', value: 'all' },
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
                { label: 'Maintenance', value: 'maintenance' },
                { label: 'Pending', value: 'pending' },
                { label: 'Completed', value: 'completed' },
                { label: 'Cancelled', value: 'cancelled' },
              ]).map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>{opt.label}</MenuItem>
              ))}
            </TextField>
          )}
          <Button
            size="small"
            onClick={resetFilters}
            startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
            disabled={!hasActiveFilters}
            sx={{
              ...tableActionButtonSx,
              height: 32,
              minHeight: 32,
              color: brandColors.textMuted,
              border: `1px solid ${brandColors.border}`,
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            Reset
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            flex: '0 0 auto',
            ml: 'auto',
            overflow: 'visible',
          }}
        >
          {columnPicker && (
            <TableColumnPicker
              allColumns={columnPicker.allColumns}
              visibleFields={columnPicker.visibleFields}
              defaultFields={columnPicker.defaultFields}
              onSave={columnPicker.onSave}
              onReset={columnPicker.onReset}
              isCustomized={columnPicker.isCustomized}
            />
          )}
          {showImportExport && (
            <TableImportExportMenus
              columns={columns}
              exportExtraColumns={exportExtraColumns}
              selectedRows={selectedRows}
              filteredRows={sorted}
              canImport={canImport}
              canExport={canExport}
              enableExcelExport={enableExcelExport}
              onImport={onImport}
              onServerExport={onExport}
              exportFileName={exportFileName}
              exportSheetName={exportSheetName}
              loading={loading}
            />
          )}
        </Box>
      </Box>

      <TableContainer sx={tableContainerSx}>
        {loading ? (
          <TableSkeleton columns={colSpan} />
        ) : (
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: Math.max(640, columns.length * 130 + (showCheckboxes ? 48 : 0) + (hasActions ? 72 : 0) + (expandable ? 48 : 0) + (showRowNumber ? 48 : 0)),
              tableLayout: 'auto',
            }}
          >
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                {showCheckboxes && (
                  <TableCell padding="checkbox" sx={{ ...tableHeadCellSx({ width: 42 }), '&::after': { display: 'none' } }}>
                    <Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected && !allPageSelected}
                      onChange={toggleAll} sx={{
                        p: 0.5,
                        color: 'rgba(255,255,255,0.75)',
                        '&.Mui-checked': { color: '#fff' },
                        '&.MuiCheckbox-indeterminate': { color: '#fff' },
                      }} />
                  </TableCell>
                )}
                {showRowNumber && (
                  <TableCell sx={tableHeadCellSx({ width: 48 })}>
                    <Box component="span" sx={tableHeadTextSx}>#</Box>
                  </TableCell>
                )}
                {expandable && (
                  <TableCell sx={tableHeadCellSx({ width: 40 })} />
                )}
                {columns.map((col) => (
                  <TableCell key={col.field} sx={tableHeadCellSx()}>
                    {col.sortable === false ? (
                      <Box component="span" sx={tableHeadTextSx}>{col.header}</Box>
                    ) : (
                      <TableSortLabel
                        active={sortBy === col.field}
                        direction={sortBy === col.field ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.field)}
                        sx={tableSortLabelSx}
                      >
                        {col.header}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell align="center" sx={tableHeadCellSx({ align: 'center', width: 56 })}>
                    <Box component="span" sx={tableHeadTextSx}>Actions</Box>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                    <InboxOutlinedIcon sx={{ fontSize: 48, color: brandColors.border, mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">{emptyMessage}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row, idx) => {
                  const id = rowId(row);
                  const isSelected = selected.includes(id);
                  const isExpanded = expandedIds.has(id);
                  return (
                    <React.Fragment key={id}>
                    <TableRow hover selected={isSelected}
                      onClick={() => {
                        if (onRowClick) onRowClick(row);
                        else if (expandable) toggleExpand(id);
                        else if (!renderActions && (onView || onRowClick)) (onView || onRowClick)?.(row);
                      }}
                      sx={{
                        cursor: onRowClick || expandable || ((onView || onRowClick) && !renderActions) ? 'pointer' : undefined,
                        bgcolor: isExpanded ? brand.green50 : isSelected ? brand.green50 : idx % 2 === 0 ? '#fff' : '#fafbfc',
                        '&.Mui-selected': { bgcolor: `${brand.green50} !important` },
                        '&:hover': { bgcolor: isExpanded ? brand.green100 : isSelected ? brand.green100 : brand.green50 },
                        transition: 'background 0.15s',
                        '& td': isExpanded ? { borderBottom: 0 } : undefined,
                      }}>
                      {showCheckboxes && (
                        <TableCell padding="checkbox" sx={tableBodyCellSx} onClick={(e) => e.stopPropagation()}>
                          <Checkbox size="small" checked={isSelected} onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleOne(id)}
                            sx={{ p: 0.5, '&.Mui-checked': { color: brand.primary } }} />
                        </TableCell>
                      )}
                      {showRowNumber && (
                        <TableCell sx={{ ...tableBodyCellSx, color: brandColors.textMuted, fontWeight: 600 }}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                      )}
                      {expandable && (
                        <TableCell sx={{ ...tableBodyCellSx, width: 40 }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={() => toggleExpand(id)} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                            <ExpandMoreIcon sx={{
                              fontSize: 20,
                              color: brand.primary,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.2s ease',
                            }} />
                          </IconButton>
                        </TableCell>
                      )}
                      {columns.map((col, colIndex) => (
                        <TableCell key={col.field} sx={tableBodyCellSx}>
                          {renderCell(col, row, colIndex)}
                        </TableCell>
                      ))}
                      {hasActions && (
                        <TableCell align="center" sx={tableBodyCellSx}
                          onClick={(e) => e.stopPropagation()}>
                          {renderActions ? renderActions(row) : (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionMenu({ anchor: e.currentTarget, row });
                              }}
                              sx={{ color: brandColors.textMuted }}
                              aria-label="Row actions"
                            >
                              <MoreVertIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                    {expandable && (
                      <TableRow>
                        <TableCell colSpan={colSpan} sx={{ py: 0, px: 0, bgcolor: '#f8fafc' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{
                              px: { xs: 2, sm: 3 },
                              py: 2,
                              borderTop: `1px solid ${brandColors.border}`,
                              borderBottom: `1px solid ${brandColors.border}`,
                            }}>
                              {renderExpandedRow?.(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Menu
        anchorEl={actionMenu?.anchor || null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        PaperProps={{ sx: { borderRadius: `${RADIUS}px`, minWidth: 160, fontFamily: APP_FONT_FAMILY } }}
      >
        {canView && onView && (
          <MenuItem onClick={() => {
            if (actionMenu?.row) onView(actionMenu.row);
            setActionMenu(null);
          }}>
            <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View" />
          </MenuItem>
        )}
        {canEdit && onEdit && (
          <MenuItem onClick={() => {
            if (actionMenu?.row) onEdit(actionMenu.row);
            setActionMenu(null);
          }}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
        )}
        {canDelete && onDelete && (
          <MenuItem onClick={() => {
            if (actionMenu?.row) onDelete(actionMenu.row);
            setActionMenu(null);
          }} sx={{ color: brandColors.danger }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[...rowsPerPageOptions]}
        onRowsPerPageChange={(e) => { onRowsPerPageChange(parseInt(e.target.value, 10)); onPageChange(0); }}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count }) => (
          `Showing ${from} to ${to} of ${count === -1 ? `more than ${to}` : count} ${recordLabel}`
        )}
        slotProps={{
          select: {
            renderValue: (value) => `${value} / page`,
          },
        }}
        sx={{
          borderTop: `1px solid ${brandColors.border}`,
          flexWrap: 'wrap',
          overflow: 'hidden',
          '.MuiTablePagination-toolbar': {
            flexWrap: 'wrap',
            gap: 0.5,
            px: { xs: 1, sm: 1.5 },
            py: 0.5,
            minHeight: '40px !important',
            height: 'auto',
            justifyContent: { xs: 'center', sm: 'space-between' },
          },
          '.MuiTablePagination-spacer': { display: 'none' },
          '.MuiTablePagination-selectLabel': { display: 'none' },
          '.MuiTablePagination-displayedRows': {
            fontSize: TYPE.secondary.fontSize,
            lineHeight: TYPE.secondary.lineHeight,
            m: 0,
            color: brandColors.textMuted,
            fontWeight: 600,
            order: -1,
            mr: 'auto',
          },
          '.MuiTablePagination-actions': { ml: { xs: 0, sm: 1 } },
          '.MuiTablePagination-input': {
            fontWeight: 700,
            fontSize: TYPE.secondary.fontSize,
            border: `1px solid ${brandColors.border}`,
            borderRadius: `${RADIUS}px`,
            px: 0.5,
          },
        }}
      />
    </Paper>
  );
};
